import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import SectionActions from './SectionActions';
import PrintHeader from './PrintHeader';
import {
  CreditCard, Users, ShieldCheck, FolderOpen, CalendarDays, BarChart3,
  ArrowRight, Clock, MapPin, Building2, Plus, Loader2, Package, Euro,
  FileText, Camera, Hammer, Truck, Receipt, Wallet, CheckCircle2, AlertCircle, Calculator
} from 'lucide-react';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [cantieri, setCantieri] = useState([]);
  const [operai, setOperai] = useState([]);
  const [timbrature, setTimbrature] = useState([]);
  const [fattureScadute, setFattureScadute] = useState([]);
  const [geoViolations, setGeoViolations] = useState([]);
  const [loading, setLoading] = useState(true);
  const pageRef = useRef(null);

  const loadData = async () => {
    try {
      const [s, c, o, t, ff, gv] = await Promise.all([
        api.getStats(), api.getCantieri(), api.getOperai(),
        api.getTimbrature(new Date().toISOString().split('T')[0]),
        api.getFattureForni('da_pagare'),
        api.getViolations(),
      ]);
      setStats(s); setCantieri(c); setOperai(o); setTimbrature(t);
      const today = new Date().toISOString().split('T')[0];
      const urgent = ff.filter(f => f.dueDate && f.dueDate <= new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0]);
      setFattureScadute(urgent);
      setGeoViolations(gv.filter(v => !v.read).slice(0, 10));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, []);

  const recentActivity = timbrature.slice(0, 6);

  const modules = [
    { path: '/app/cantieri', icon: Building2, label: 'Gestione Cantieri', desc: 'Cantieri, personale, materiali, giornale lavori e foto', color: '#3b82f6', bg: '#eff6ff' },
    { path: '/app/badge', icon: CreditCard, label: 'Badge Cantiere', desc: 'Timbratura ingresso/uscita, badge QR digitali', color: '#0ea5e9', bg: '#f0f9ff' },
    { path: '/app/presenze', icon: Users, label: 'Presenze', desc: 'Monitoraggio presenze, ore lavorate e costi giornalieri', color: '#22c55e', bg: '#f0fdf4' },
    { path: '/app/appuntamenti', icon: CalendarDays, label: 'Appuntamenti', desc: 'Calendario appuntamenti con clienti e cantieri', color: '#10b981', bg: '#ecfdf5' },
    { path: '/app/magazzino', icon: Package, label: 'Magazzino', desc: 'Materiali, attrezzature, mezzi e confronto prezzi', color: '#f59e0b', bg: '#fffbeb' },
    { path: '/app/paga', icon: Euro, label: 'Registro Paga', desc: 'Calcolo paghe, acconti, saldi e grafici per operaio', color: '#8b5cf6', bg: '#f5f3ff' },
    { path: '/app/documenti', icon: FolderOpen, label: 'Documenti', desc: 'Archivio documenti, pagamenti, preventivi e fatture', color: '#ec4899', bg: '#fdf2f8' },
    { path: '/app/sicurezza', icon: ShieldCheck, label: 'Sicurezza', desc: 'Checklist sicurezza e conformità normativa cantiere', color: '#ef4444', bg: '#fef2f2' },
    { path: '/app/pianificazione', icon: CalendarDays, label: 'Pianificazione', desc: 'Cronoprogramma lavori e gestione attività', color: '#14b8a6', bg: '#f0fdfa' },
    { path: '/app/reportistica', icon: BarChart3, label: 'Reportistica', desc: 'Analytics, grafici e report di cantiere', color: '#6366f1', bg: '#eef2ff' },
    { path: '/app/fatture', icon: FileText, label: 'Fatture Fornitori', desc: 'Gestione fatture, scadenze e pagamenti', color: '#ef4444', bg: '#fef2f2' },
    { path: '/app/computo', icon: Calculator, label: 'Computo e SAL', desc: 'Computo metrico, misure e avanzamento lavori', color: '#0d9488', bg: '#f0fdfa' },
    { path: '/app/analisi-costi', icon: BarChart3, label: 'Analisi Costi', desc: 'Costi per cantiere, operaio e materiale con grafici', color: '#7c3aed', bg: '#f5f3ff' },
  ];

  if (loading) return <div className="page-container"><div className="loading-center"><Loader2 size={32} className="spin" /></div></div>;

  return (
    <div className="page-container" ref={pageRef}>
      <PrintHeader title="Dashboard" subtitle="Riepilogo generale Colabella Group" />
      <div className="page-header">
        <div className="header-left">
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Benvenuto in Colabella Group</p>
        </div>
        <div className="header-right">
          <SectionActions sectionTitle="Dashboard" contentRef={pageRef} />
          <div className="header-date">
            <Clock size={16} />
            <span>{new Date().toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="dash-stats">
        <div className="dash-stat dash-stat-blue">
          <div className="dash-stat-icon"><Users size={24} /></div>
          <div className="dash-stat-content">
            <span className="dash-stat-value">{stats?.onSite || 0}<span className="dash-stat-total">/{stats?.totalOperai || 0}</span></span>
            <span className="dash-stat-label">Operai in Cantiere</span>
          </div>
        </div>
        <div className="dash-stat dash-stat-green">
          <div className="dash-stat-icon"><Clock size={24} /></div>
          <div className="dash-stat-content">
            <span className="dash-stat-value">{stats?.todayTimbrature || 0}</span>
            <span className="dash-stat-label">Timbrature Oggi</span>
          </div>
        </div>
        <div className="dash-stat dash-stat-amber">
          <div className="dash-stat-icon"><ShieldCheck size={24} /></div>
          <div className="dash-stat-content">
            <span className="dash-stat-value">{stats?.checklistDone || 0}<span className="dash-stat-total">/{stats?.checklistTotal || 0}</span></span>
            <span className="dash-stat-label">Sicurezza</span>
          </div>
        </div>
        <div className="dash-stat dash-stat-purple">
          <div className="dash-stat-icon"><Building2 size={24} /></div>
          <div className="dash-stat-content">
            <span className="dash-stat-value">{stats?.cantieriAttivi || 0}</span>
            <span className="dash-stat-label">Cantieri Attivi</span>
          </div>
        </div>
      </div>

      {/* Alert Fatture */}
      {fattureScadute.length > 0 && (
        <Link to="/app/fatture" className="dash-alert-box">
          <div className="dash-alert-icon"><AlertCircle size={24} /></div>
          <div className="dash-alert-content">
            <strong>{fattureScadute.length} fatture in scadenza / scadute</strong>
            <span>€{fattureScadute.reduce((s, f) => s + (f.amount || 0), 0).toFixed(2)} da pagare — Clicca per gestire</span>
          </div>
          <ArrowRight size={20} />
        </Link>
      )}

      {/* Alert Geofence Violations */}
      {geoViolations.length > 0 && (
        <div className="dash-violations">
          <div className="dash-viol-header" onClick={async () => { await api.markViolationsRead(); loadData(); }}>
            <div className="dash-viol-icon"><MapPin size={22} /></div>
            <div className="dash-viol-title">
              <strong>{geoViolations.length} violazioni georecinto</strong>
              <span>Clicca per confermare lettura</span>
            </div>
          </div>
          <div className="dash-viol-list">
            {geoViolations.map(v => (
              <div key={v.id} className="dash-viol-item">
                <div className="dash-viol-dot" />
                <div className="dash-viol-info">
                  <span className="dash-viol-name">{v.workerName}</span>
                  <span className="dash-viol-detail">{v.distance}m da {v.cantiereName} (limite {v.radius}m)</span>
                </div>
                <span className="dash-viol-time">{new Date(v.timestamp).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</span>
                <span className="dash-viol-date">{new Date(v.timestamp).toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modules Grid */}
      <div className="dash-section">
        <h2 className="dash-section-title">Accesso Rapido</h2>
        <div className="dash-modules">
          {modules.map((mod) => {
            const Icon = mod.icon;
            return (
              <Link key={mod.path} to={mod.path} className="dash-module">
                <div className="dash-module-icon" style={{ background: mod.bg, color: mod.color }}>
                  <Icon size={26} />
                </div>
                <div className="dash-module-content">
                  <h3>{mod.label}</h3>
                  <p>{mod.desc}</p>
                </div>
                <ArrowRight size={18} className="dash-module-arrow" style={{ color: mod.color }} />
              </Link>
            );
          })}
        </div>
      </div>

      {/* Bottom Grid */}
      <div className="dash-bottom">
        {/* Recent Activity */}
        <div className="dash-card">
          <h3 className="dash-card-title">Attività Recente</h3>
          <div className="dash-activity">
            {recentActivity.map((item) => (
              <div key={item.id} className="dash-activity-item">
                <div className={`dash-act-dot ${item.type === 'ingresso' ? 'dot-green' : 'dot-red'}`} />
                <div className="dash-act-info">
                  <span className="dash-act-name">{item.workerName}</span>
                  <span className="dash-act-detail">{item.type === 'ingresso' ? 'Ingresso' : 'Uscita'} — {item.cantiereName}</span>
                </div>
                <span className="dash-act-time">
                  {new Date(item.timestamp).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            ))}
            {recentActivity.length === 0 && <p className="empty-text">Nessuna attività oggi</p>}
          </div>
        </div>

        {/* Cantieri */}
        <div className="dash-card">
          <div className="dash-card-header">
            <h3 className="dash-card-title">Cantieri</h3>
            <Link to="/app/cantieri" className="dash-card-link">Vedi tutti <ArrowRight size={14} /></Link>
          </div>
          <div className="dash-cantieri">
            {cantieri.slice(0, 4).map((c) => (
              <Link to="/app/cantieri" key={c.id} className="dash-cantiere">
                <div className="dash-cant-left">
                  <Building2 size={18} className="dash-cant-icon" />
                  <div>
                    <h4>{c.name}</h4>
                    <span><MapPin size={11} /> {c.address}</span>
                  </div>
                </div>
                <div className="dash-cant-right">
                  <span className={`dash-cant-status ${c.status === 'attivo' ? 'dcs-active' : 'dcs-paused'}`}>
                    {c.status === 'attivo' ? 'Attivo' : 'Pausa'}
                  </span>
                  <span className="dash-cant-workers"><Users size={13} /> {c.workers || 0}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
